import {useCallback, useEffect, useRef, useState} from "react";
import play, {MatchSound} from "./sounds";
import {ParsedMatchTiming, renderTime} from "./utils";
import { Temporal } from "temporal-polyfill";

interface SoundCue {
    trigger: Temporal.Duration,
    sound: MatchSound
}

interface TimerParams {
    startAt?: number | null,
    duration?: Temporal.Duration,
    nowCallback: () => number,
    sounds?: SoundCue[]
}

export interface TimerData {
    remaining: number,
    running: boolean,
    temporarilySilenced? : boolean
}

export interface MatchTimerData extends TimerData {
    displayTime: string,
    preMatch: boolean,
    auto: boolean,
    transition: boolean,
    teleop: boolean,
    pastMidmatch: boolean,
    endgame: boolean,
    done: boolean
}

export function useTimer({
    startAt,
    duration,
    nowCallback,
    sounds
} : TimerParams) : TimerData {
    const realDuration = duration ? duration : Temporal.Duration.from('PT0S');
    const [remaining, setRemaining] = useState(realDuration.total('seconds'));
    const rafRef = useRef<number | null>(null);
    const soundTimeouts = useRef<number[]>([]);
    const tickRef = useRef<() => void>(() => {});

    tickRef.current = useCallback(() => {
        const remaining = realDuration.subtract({milliseconds: Math.round(nowCallback() - startAt!)})

        setRemaining(Math.max(0, Math.ceil(remaining.total('seconds'))))
        if(remaining.sign > 0) {
            rafRef.current = requestAnimationFrame(tickRef.current);
        } else {
            rafRef.current = null;
        }
    }, [realDuration, startAt])

    useEffect(() => {
        if(!startAt) {
            return
        }

        soundTimeouts.current = sounds ? sounds.map(s => {
            const timeoutDelay = (realDuration.subtract(s.trigger).total('milliseconds')) - (nowCallback() - startAt!)
            // Play sounds that should have already been played if we are within 5 seconds of trigger time (to allow for page reload right at match start etc)
            if(timeoutDelay < 0) {
                if(timeoutDelay > -5000) {
                    play(s.sound);
                }
                return
            }
            return window.setTimeout(() => {
                play(s.sound);
            }, timeoutDelay);
        }).filter(t => t !== undefined) : [];
        setRemaining(realDuration.total('seconds'))
        rafRef.current = requestAnimationFrame(tickRef.current);
        return () => {
            if(rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
            soundTimeouts.current.forEach(t => clearTimeout(t));
        }
    }, [realDuration.toString(), startAt])

    return { remaining: remaining, running: !!startAt && remaining > 0 };
}

// From https://overreacted.io/making-setinterval-declarative-with-react-hooks/
export function useInterval(callback: () => void, delay : number | null) {
    const savedCallback = useRef<() => void>();

    // Remember the latest callback.
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    // Set up the interval.
    useEffect(() => {
        function tick() {
            if(savedCallback.current) {
                savedCallback.current();
            }
        }
        if (delay !== null) {
            let id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
}

export function useMatchTimer(paramsObj : Omit<TimerParams, "duration" | "sounds"> & {
    withSound?: boolean
}, timing?: ParsedMatchTiming) : MatchTimerData {
    const timer = useTimer({...paramsObj,
        duration: timing ? timing.total : undefined,
        sounds: timing && paramsObj.withSound ? [
            { trigger: timing.total.subtract(timing.countdown).add({seconds: 5, milliseconds: 150}), sound: 'matchCountdown' },
            { trigger: timing.total.subtract(timing.countdown), sound: 'matchStart' },
            { trigger: timing.teleop.add(timing.transition), sound: 'autoEnd' },
            { trigger: timing.teleop.add(timing.transition).subtract({seconds: 2}), sound: 'controllers' },
            { trigger: timing.teleop.add({seconds: 3}), sound: 'countdown' },
            { trigger: timing.teleop, sound: 'startTeleop' },
            { trigger: timing.midmatchWarning, sound: 'midmatchWarning' },
            { trigger: timing.endgameWarning, sound: 'endgameWarning' },
            { trigger: Temporal.Duration.from({seconds: 0}), sound: 'matchEnd' },
        ] : []
    });
    if(!timing) {
        return {
            ...timer,
            displayTime: renderTime(0),
            preMatch: false,
            auto: false,
            transition: false,
            teleop: false,
            endgame: false,
            pastMidmatch: false,
            done: false
        }
    }

    const matchSeconds = timing.total.subtract(timing.countdown).total('seconds')
    const teleopSeconds = timing.teleop.total('seconds')
    const transitionSeconds = timing.transition.total('seconds')
    const endgameSeconds = timing.endgameWarning.total('seconds')
    let timeString = ''
    const states = {
        preMatch: timer.remaining > matchSeconds,
        auto: timer.running && timer.remaining > (teleopSeconds + transitionSeconds) && timer.remaining <= matchSeconds,
        transition: timer.running && timer.remaining > teleopSeconds && timer.remaining <= (teleopSeconds + transitionSeconds),
        teleop: timer.running && timer.remaining <= teleopSeconds,
        endgame: timer.running && timer.remaining > 0 && timer.remaining <= endgameSeconds,
        pastMidmatch: timer.running && timer.remaining > 0 && timer.remaining <= timing.midmatchWarning.total('seconds'),
        done: !timer.running && timer.remaining <= 0
    }

    if(paramsObj.startAt) {
        if (states.preMatch) {
            timeString = renderTime(timer.remaining - timing.total.subtract(timing.countdown).total('seconds'))
        } else if (states.auto) {
            timeString = renderTime(timer.remaining - timing.transition.total('seconds'))
        } else if (states.transition) {
            timeString = renderTime(timer.remaining - timing.teleop.total('seconds'))
        } else if (states.teleop) {
            timeString = renderTime(timer.remaining)
        } else if (states.done) {
            timeString = renderTime(0)
        }
    } else {
        timeString = renderTime(timing.total.subtract(timing.countdown).subtract(timing.transition).total('seconds'))
    }
    return {
        ...timer,
        ...states,
        displayTime: timeString
    }
}
